import { __decorate } from "tslib";
import Button from '@mui/material/Button';
import Grid from '@mui/material/GridLegacy';
import Typography from '@mui/material/Typography';
import autobind from 'autobind-decorator';
import classnames from 'classnames';
import React, { useMemo, Component } from 'react';
import { ArrowDown, ArrowUp } from '../../icons/Arrows';
import { getTrace, withTrace } from '../../utils/trace';
import { AttachedMenu, Menu } from '../Menu';
import { useSelectedLabelWidth, useStyleOnePaperWidth, useStyles } from './SelectMenu.styles';
const applyTrace = getTrace('select-menu');
const selectMenuItem = (label, key) => {
    if (typeof label === 'string') {
        return () => (React.createElement(Typography, Object.assign({ color: "inherit", variant: "body2" }, applyTrace(`menu-item-${key}`)), label));
    }
    return label;
};
function SelectedLabel(props) {
    const { label: Label, classes, disabled, labelPrefix, labelWidth = 0 } = props;
    const { classes: selectedLabelWidthClass } = useSelectedLabelWidth({ labelWidth });
    if (typeof Label === 'string') {
        return (React.createElement(Typography, Object.assign({ noWrap: true, align: "right", color: "inherit", variant: "body2" }, applyTrace('item-label'), { className: classnames({ [classes.disabledSelected]: disabled }, { [selectedLabelWidthClass.maxLabelWidth]: labelWidth }) }), labelPrefix ? `${labelPrefix}${Label}` : Label));
    }
    return (React.createElement(Grid, { className: classnames({ [classes.disabledLabel]: disabled }, { [selectedLabelWidthClass.maxLabelWidth]: labelWidth }) },
        React.createElement(Label, Object.assign({}, applyTrace('item-label-component')))));
}
const generateMenuItems = (options, selected, action) => options.map(({ key, label, ItemComponent = selectMenuItem(label, key.toString()), }) => ({
    key,
    ItemComponent,
    label,
    selected: selected ? key === selected : false,
    action: () => action(key),
}));
const getValueOrDefault = (value, defaultValue) => {
    return defaultValue === undefined ? value : defaultValue;
};
const GenerateDropdown = (props) => {
    const { options, optionGroups, disabled = false, fitContent = false, applyTrace, applyTraceMessage, buttonFullWidth = true, anchorRight = false, defaultValue, value, open, selectedValue, anchorEl, anchorReference, handleSelection, toggleOpen, menuType = '2', setWidth = 0, labelWidth = 0, popoverClasses, popperClassName, labelPrefix = null, shouldAttachMenuToAnchor = false, popperPlacement = 'bottom-start', annotationsClassName, actionButton, customHeight, } = props;
    const { classes } = useStyles(undefined, { props: { classes: props.classes } });
    const menuItems = useMemo(() => generateMenuItems(options, getValueOrDefault(value, defaultValue), handleSelection), [options, defaultValue, value, handleSelection]);
    const showMenu = open && !disabled;
    const transformOrigin1 = anchorRight ? { horizontal: 'right', vertical: 'top' } : undefined;
    const transformOrigin2 = { vertical: 'top', horizontal: 'left' };
    const transformOrigin = menuType === '2' ? transformOrigin2 : transformOrigin1;
    const offsetFromRight = 21;
    const anchorStyle2 = anchorRight && anchorEl
        ? {
            horizontal: anchorEl.offsetWidth - offsetFromRight,
            vertical: 'top',
        }
        : undefined;
    const anchorStyle1 = { vertical: 'bottom', horizontal: 'left' };
    const anchorOrigin = menuType === '2' ? anchorStyle2 : anchorStyle1;
    const justifySpacing = menuType === '2' ? 'flex-start' : 'space-between';
    const width = setWidth;
    let applyTraceMessageValue = '';
    if (applyTraceMessage) {
        applyTraceMessageValue = `${applyTraceMessage}-`;
    }
    const MenuComponent = shouldAttachMenuToAnchor ? AttachedMenu : Menu;
    const Icon = showMenu ? ArrowUp : ArrowDown;
    const { classes: styleOnePaperWidthClass } = useStyleOnePaperWidth({ width });
    return (React.createElement(Grid, { container: true, justifyContent: "flex-end", className: classnames({
            [classes.fitContent]: fitContent,
        }) },
        React.createElement(Grid, { container: true, item: true, xs: true },
            React.createElement(Button, Object.assign({ fullWidth: buttonFullWidth, variant: "text", onClick: toggleOpen, className: classnames({
                    [classes.button]: menuType === '2',
                    [classes.styleOneButton]: menuType === '1',
                    [classes.styleOneSelected]: menuType === '1' && showMenu,
                    [styleOnePaperWidthClass.setWidth]: setWidth !== 0,
                }), disabled: disabled }, applyTrace(`${applyTraceMessageValue}btn`)),
                React.createElement(Grid, { container: true, wrap: "nowrap", alignItems: "center", justifyContent: justifySpacing, spacing: 1, className: classnames({ [classes.selected]: menuType === '2' && showMenu }) },
                    React.createElement(Grid, { item: true, container: true, alignItems: "center", xs: true, className: classes.labelContainer },
                        React.createElement(SelectedLabel, { classes: classes, disabled: disabled, label: selectedValue, labelPrefix: labelPrefix, labelWidth: labelWidth })),
                    React.createElement(Grid, { item: true },
                        React.createElement(Icon, { color: "inherit", fontSize: "small", className: classes.icon }))))),
        React.createElement(MenuComponent, Object.assign({ open: showMenu, options: menuItems, optionGroups: optionGroups, onClose: toggleOpen, classes: {
                paper: classnames({
                    [classes.paper]: menuType === '2',
                    [classes.styleOnePaper]: menuType === '1',
                    [styleOnePaperWidthClass.menuWidth]: menuType === '1',
                }),
                menuItem: classnames({ [classes.styleOneMenuItem]: menuType === '1' }, classes.menuItem, annotationsClassName),
                menuLabel: classes.menuLabel,
            }, disableAutoFocusItem: true, anchorEl: anchorEl, anchorReference: anchorReference, anchorOrigin: anchorOrigin, transformOrigin: transformOrigin, popoverClasses: popoverClasses, popperClassName: popperClassName, popperPlacement: popperPlacement, actionButton: actionButton, customHeight: customHeight }, applyTrace('menu')))));
};
export class SelectMenuBase extends Component {
    constructor(props) {
        super(props);
        const { defaultValue, value } = this.props;
        const { label = '' } = this.optionByKey(getValueOrDefault(value, defaultValue)) || {};
        this.state = {
            selectedValue: label,
            open: false,
            anchorEl: null,
            anchorReference: 'none',
        };
    }
    componentDidUpdate(prevProps) {
        const { defaultValue } = this.props;
        if (prevProps.defaultValue !== defaultValue) {
            const { label = '' } = this.optionByKey(defaultValue) || {};
            this.setState({
                selectedValue: label,
            });
        }
    }
    toggleOpen(event) {
        const { currentTarget: anchorEl = null } = event || {};
        const { open } = this.state;
        let anchorReference = 'none';
        if (anchorEl && !open) {
            anchorReference = 'anchorEl';
        }
        this.setState({ anchorEl, anchorReference, open: !open && anchorEl !== null });
    }
    optionByKey(findKey) {
        const { options } = this.props;
        return options.find(({ key }) => key === findKey);
    }
    handleSelection(selected) {
        const { defaultValue, onSelect } = this.props;
        if (this.checkUncontrolled(defaultValue)) {
            const { label = '' } = this.optionByKey(selected) || {};
            this.setState({ selectedValue: label, open: false });
        }
        else {
            this.setState((prevState) => (Object.assign(Object.assign({}, prevState), { open: false })));
        }
        onSelect(selected);
    }
    checkUncontrolled(defaultValue) {
        return defaultValue !== undefined;
    }
    render() {
        const { open, selectedValue, anchorEl, anchorReference } = this.state;
        return (React.createElement(GenerateDropdown, Object.assign({}, this.props, { open,
            selectedValue,
            anchorEl,
            anchorReference,
            toggleOpen: this.toggleOpen,
            handleSelection: this.handleSelection })));
    }
}
__decorate([
    autobind
], SelectMenuBase.prototype, "toggleOpen", null);
__decorate([
    autobind
], SelectMenuBase.prototype, "handleSelection", null);
export const SelectMenu = withTrace('select-menu')(SelectMenuBase);
//# sourceMappingURL=SelectMenu.js.map